import { Injectable } from '@angular/core';
import { Observable, filter, timer, Subscription } from 'rxjs';
import { WebSocketSubject, webSocket } from 'rxjs/webSocket';
import { environment } from 'src/environments/environment';
import { LiteralService } from '../literal/literal.service';
import { PhaseStatus } from 'src/app/models/enums';

const WS_NOT_JSON = Symbol('WS_NOT_JSON');

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  private socket$: WebSocketSubject<any> | undefined;
  private ttlSub: Subscription | undefined;

  constructor(private literalService: LiteralService) {}

  // Phase names arrive as enum keys; the entity being processed is appended when the backend sends one
  progressDetail(message: any): string {
    const phaseKey = message?.phase_name ? `progress.phases.${message.phase_name}` : '';
    const translated = phaseKey ? this.literalService.get(phaseKey) : '';
    const phase = translated && translated !== phaseKey ? this.literalService.get(phaseKey, true) : (message?.phase_name ?? '');
    const entityName = message?.progress_entity_data?.name;
    return entityName ? `${phase}: ${entityName}` : phase;
  }

  // Every phase ends with a COMPLETED message; only the one at 100%, a failure or a plain result end the operation
  isOperationCompleted(message: any): boolean {
    if (!message || typeof message !== 'object') return false;
    if (!('phase_status' in message)) return true;
    return message.phase_status === PhaseStatus.FAILED
      || (message.phase_status === PhaseStatus.COMPLETED && message.total_percentage === 100);
  }

  isIntermediatePhaseCompletion(message: any): boolean {
    return message?.phase_status === PhaseStatus.COMPLETED && !this.isOperationCompleted(message);
  }

  connect(operationId: string, sha: string, ttlMs: number = 60000): void {
    this.closeConnection();
    const url = `${environment.webSocketUrl}?operation-id=${encodeURIComponent(operationId)}&sha=${encodeURIComponent(sha)}`;
    this.socket$ = webSocket({
      url,
      deserializer: (e: MessageEvent) => {
        try {
          if (e.data === 'DISCONNECTED') {
            this.closeConnection();
            console.info('WebSocket disconnected by server');
          }
          return JSON.parse(e.data);
        } catch {
          return WS_NOT_JSON;
        }
      },
    });
    // this.ttlSub = timer(ttlMs).subscribe(() => this.closeConnection());
  }

  sendMessage(message: any): void {
    this.socket$?.next(message);
  }

  getMessages(): Observable<any> {
    return this.socket$!.asObservable().pipe(
      filter((msg) => msg !== WS_NOT_JSON)
    );
  }

  closeConnection(): void {
    this.ttlSub?.unsubscribe();
    this.ttlSub = undefined;
    if (this.socket$) {
      this.socket$.complete();
      this.socket$ = undefined;
    }
  }
}
